<template>
  <v-dialog :value="value" :width="drawerWidth" content-class="widget-explanation-dialog"
    transition="slide-x-reverse-transition" aria-label="About this widget" @input="onDialogInput">
    <v-card class="widget-about-panel">
      <header class="wap-header">
        <div><h2>About</h2><p class="wap-title">{{ displayTitle }}</p></div>
        <v-btn ref="closeButton" icon aria-label="Close explanation" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </header>
      <div v-if="hasSelectedScope" class="wap-scope">
        <v-btn-toggle :value="scopeMode" mandatory dense @change="$emit('scope-change', $event)">
          <v-btn small value="all">Whole chart</v-btn><v-btn small value="selected">Selected group</v-btn>
        </v-btn-toggle>
        <p v-if="scopeMode === 'selected'">{{ selectedScopeSummary }}</p>
      </div>
      <v-divider />
      <div class="wap-body" aria-live="polite" :aria-busy="loading ? 'true' : 'false'">
        <div v-if="loading" class="wap-state" role="status">
          <v-progress-circular indeterminate size="28" width="3" color="primary" /><p>Loading the explanation…</p>
        </div>
        <div v-else-if="error" class="wap-state" role="alert">
          <v-icon color="error">mdi-alert-circle-outline</v-icon><p>The explanation could not be loaded.</p>
          <v-btn small outlined color="primary" @click="$emit('retry')">Try again</v-btn>
        </div>
        <div v-else-if="unsupported" class="wap-state" role="status">
          <v-icon>mdi-information-outline</v-icon>
          <p>{{ filtersUnavailable ? explanation.unsupportedReason : 'An automatic explanation is not available for this calculation yet.' }}</p>
          <v-btn v-if="filtersUnavailable" small outlined color="primary" @click="$emit('retry')">Retry with current filters</v-btn>
          <p v-if="aboutText">{{ aboutText }}</p>
        </div>
        <template v-else-if="calculation">
          <p v-if="aboutText" class="wap-description">{{ aboutText }}</p>
          <section>
            <div class="wap-calculation-heading">
              <h3>What this shows</h3>
              <strong v-if="calculation.result != null || !groupResults.length" class="wap-result">{{ displayValue(calculation.result) }}</strong>
            </div>
            <p>{{ calculationSummary }}</p>
            <dl v-if="calculationFacts.length" class="wap-facts">
              <div v-for="fact in calculationFacts" :key="fact.label"><dt>{{ fact.label }}</dt><dd>{{ fact.value }}</dd></div>
            </dl>
            <div v-if="groupResults.length" class="wap-groups">
              <div v-for="(group, index) in groupResults" :key="index"><span>{{ groupLabel(group) }}</span><strong>{{ displayValue(group.value) }}</strong></div>
              <p v-if="calculation.groupsTruncated" class="wap-muted">Select a group in the chart to see its own explanation.</p>
            </div>
          </section>
          <section v-if="derivation">
            <h3>{{ derivation.title || 'How it is calculated' }}</h3>
            <p v-if="derivation.formula">{{ derivation.formula }}</p>
            <p v-if="governingLabel"><strong>Value comes from:</strong> {{ governingLabel }}</p>
            <dl v-if="derivation.inputs && derivation.inputs.length" class="wap-facts">
              <div v-for="input in derivation.inputs" :key="input.label"><dt>{{ input.label }}</dt><dd>{{ displayInput(input.value) }}<template v-if="input.unit"> {{ input.unit }}</template></dd></div>
            </dl>
            <p v-if="derivation.note" class="wap-muted">{{ derivation.note }}</p>
          </section>
          <section v-if="scope">
            <h3>Why these values are included</h3>
            <p v-if="scope.sourceLabel"><strong>Source:</strong> {{ scope.sourceLabel }}</p>
            <ul v-if="scopeFilters.length" class="wap-filters"><li v-for="(filter, index) in scopeFilters" :key="index">{{ filter }}</li></ul>
            <p v-else>The current dashboard filters determine which records are included.</p>
            <p v-if="windowDescription">{{ windowDescription }}</p>
            <p v-if="scope.selfSelectionExcluded" class="wap-muted">This chart’s selection filters the other widgets. Choose Selected group above to explain just that selection.</p>
          </section>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
import { formatDashboardNumber, metricIsKnown, filterConditionLabel, selectionColumnLabel } from '@/features/Dashboard/utils/dashboardReader';
export default {
  name: 'WidgetExplanationDrawer',
  props: {
    value: Boolean, explanation: { type: Object, default: null }, loading: Boolean, error: Boolean,
    widgetTitle: { type: String, default: '' }, scopeMode: { type: String, default: 'all' },
    selection: { type: Object, default: null },
  },
  computed: {
    drawerWidth() { return this.$vuetify?.breakpoint?.smAndDown ? '100%' : 520; },
    displayTitle() { return this.explanation?.widget?.title || this.widgetTitle || 'Dashboard widget'; },
    aboutText() { return this.explanation?.widget?.aboutText || ''; },
    unsupported() { return this.explanation?.status === 'unsupported'; },
    filtersUnavailable() { return this.explanation?.unsupportedCode === 'currentFiltersUnavailable'; },
    calculation() { return this.explanation?.calculation || null; },
    scope() { return this.explanation?.scope || null; },
    groupResults() { return this.calculation?.groupResults || []; },
    hasSelectedScope() { return !!this.selection?.filterColumn && !!this.selection?.values?.length; },
    selectedScopeSummary() {
      return selectionColumnLabel(this.selection || {}) + ': ' + (this.selection?.values || []).map(item => item.display ?? item.value ?? 'Not set').join(', ');
    },
    calculationSummary() {
      const field = this.calculation?.metricLabel || 'the recorded value';
      const fn = this.calculation?.function;
      if (fn === 'COUNT' && /id$/i.test(this.calculation?.metricColumn || '')) return 'Counts each matching record with a recorded reference. Records are counted separately, even when they share a name.';
      return {
        ROWS: 'Counts the records that match the current filters.',
        COUNT: 'Counts records where ' + field + ' is available. Missing values are excluded.',
        COUNT_DISTINCT: 'Counts each different ' + field + ' once, however many records share it. Missing values are excluded.',
        SUM: 'Adds the available values of ' + field + '. Missing values are excluded.',
        AVG: 'Adds the available values of ' + field + ', then divides by the number of records with a known value.',
        MIN: 'Shows the lowest available ' + field + ' among the matching records. Missing values are excluded.',
        MAX: 'Shows the highest available ' + field + ' among the matching records. Missing values are excluded.',
      }[fn] || 'Shows the value calculated from this widget’s saved settings and current filters.';
    },
    calculationFacts() {
      const c = this.calculation;
      if (!c) return [];
      const facts = [{ label: 'Records included', value: this.displayValue(c.matchedRowCount) }];
      if (c.nullInputCount > 0) {
        facts.push({ label: 'With a known value', value: this.displayValue(c.nonNullInputCount) });
        facts.push({ label: 'Missing values excluded', value: this.displayValue(c.nullInputCount) });
      }
      if (c.function === 'AVG') {
        facts.push({ label: 'Total of available values', value: this.displayValue(c.sum) });
        facts.push({ label: 'Divided by', value: this.displayValue(c.denominator) });
      }
      return facts;
    },
    derivation() {
      const detail = this.explanation?.derivation;
      return ['canonicalFormula', 'storedSnapshot'].includes(detail?.kind) ? detail : null;
    },
    governingLabel() { return this.calculation?.governingRecord?.displayLabel || ''; },
    scopeFilters() {
      return (this.scope?.filters || []).filter(filter => filter.origin !== 'dashboard' || filter.column)
        .map(filter => filterConditionLabel(filter, filter.label));
    },
    windowDescription() {
      const window = this.scope?.window;
      if (!window?.count || !window.mode) return '';
      return 'Includes the ' + (window.mode === 'bottom' ? 'bottom' : 'top') + ' ' + window.count + ' records under the selected ordering.';
    },
  },
  mounted() { this.$nextTick(() => this.$refs.closeButton?.$el?.focus?.()); },
  methods: {
    displayValue(value) { return formatDashboardNumber(value); },
    displayInput(value) { return metricIsKnown(value) ? formatDashboardNumber(value) : String(value ?? 'Not available'); },
    groupLabel(group) { return (group.groupValues || []).map(item => item.value ?? 'Not set').join(' · '); },
    onDialogInput(open) { if (!open) this.$emit('close'); },
  },
};
</script>
<style scoped>
.widget-about-panel { height: 100%; display: flex; flex-direction: column; border-radius: 0; border-top: 3px solid var(--ds-brand-castleton, #03624c); }
.wap-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 18px 20px 14px; flex-shrink: 0; }
.wap-header h2 { margin: 0; font-size: 19px; line-height: 1.4; }
.wap-title { margin: 4px 0 0; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.wap-scope { padding: 0 20px 14px; flex-shrink: 0; }
.wap-scope p { margin: 8px 0 0; font-size: 12px; overflow-wrap: anywhere; }
.wap-body { padding: 18px 20px 28px; flex: 1 1 auto; min-height: 0; overflow-y: auto; font-size: 13px; line-height: 1.6; }
.wap-body section + section { border-top: 1px solid rgba(70, 90, 80, .15); margin-top: 20px; padding-top: 18px; }
.wap-body h3 { font-size: 14px; margin: 0 0 8px; }
.wap-body p { margin: 8px 0; overflow-wrap: anywhere; }
.wap-calculation-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.wap-result { font-size: 24px; }
.wap-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 16px; margin: 14px 0 0; }
.wap-facts dt, .wap-muted { opacity: .75; font-size: 12px; }
.wap-facts dd { margin: 2px 0 0; font-weight: 600; }
.wap-groups { margin-top: 14px; }
.wap-groups > div { display: flex; justify-content: space-between; gap: 16px; padding: 6px 0; }
.wap-groups span { overflow-wrap: anywhere; }
.wap-filters { padding-left: 18px; }
.wap-filters li + li { margin-top: 6px; }
.wap-state { display: flex; min-height: 180px; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 10px; }
</style>
<style>
.v-dialog.widget-explanation-dialog { margin: 0 0 0 auto; height: 100%; max-height: 100%; max-width: 100vw; border-radius: 0; }
</style>
